import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ChevronRight, Hourglass, ShieldAlert, Upload } from 'lucide-react';
import clsx from 'clsx';
import { buildContractRoleQuickActionRoutes } from '@utils/contractRoleDashboardRoutes';
import type { ContractRoleQuickActionsData } from '@app-types';
import { DashboardSectionError } from './DashboardSectionError';
import styles from './ProgramLeadQuickActions.module.css';
import tones from './AssessorQuickActions.module.css';

interface ContractRoleQuickActionsProps {
  data: ContractRoleQuickActionsData | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

const ContractRoleQuickActions: React.FC<ContractRoleQuickActionsProps> = ({ data, isLoading, isError, onRetry }) => {
  const navigate = useNavigate();

  // Without data there is no server window to build the Due Today/Tomorrow dates from, so tiles stay inert until it arrives.
  const routes = data ? buildContractRoleQuickActionRoutes(data) : undefined;

  const actions = [
    { label: 'Evidence Pending Submission', count: data?.evidence_pending_submission, icon: Upload, tone: tones.iconWarning, route: routes?.evidencePendingSubmission },
    { label: 'Evidence Due Today/Tomorrow', count: data?.evidence_due_soon, icon: Hourglass, tone: tones.iconWarning, route: routes?.evidenceDueSoon },
    { label: 'Evidence Past Due', count: data?.evidence_past_due, icon: AlertCircle, tone: tones.iconDanger, route: routes?.evidencePastDue },
    { label: 'Evidence Escalated', count: data?.evidence_escalated, icon: ShieldAlert, tone: tones.iconDanger, route: routes?.evidenceEscalated }
  ];

  return (
    <div className={styles.quickActionsContainer}>
      <div className={styles.actionsHeader}>
        <h3>Quick Actions</h3>
        <p>Evidence you still need to submit</p>
      </div>
      {isError ? (
        <DashboardSectionError
          message="Failed to load evidence quick actions."
          onRetry={onRetry}
          dataTestId="contract-role-quick-actions-error"
        />
      ) : (
      <div className={styles.actionsGrid}>
        {actions.map((action) => {
          const IconComponent = action.icon;
          return (
            <button
              type="button"
              key={`action-${action.label}`}
              className={clsx(styles.actionTile, tones.tile)}
              disabled={!action.route}
              onClick={() => action.route && navigate(action.route)}
            >
              <div className={clsx(styles.actionIcon, action.tone)}>
                <IconComponent size={24} />
              </div>
              <div className={styles.actionContent}>
                <p className={styles.actionLabel}>{action.label}</p>
                <p className={styles.actionCount}>{isLoading ? '–' : (action.count ?? 0)}</p>
              </div>
              <ChevronRight size={20} className={styles.actionArrow} />
            </button>
          );
        })}
      </div>
      )}
    </div>
  );
};

export { ContractRoleQuickActions };
